import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '../../i18n/nl.js';
import { useAuth } from '../../contexts/AuthContext';
import { fetchNietGelogdeSessies, UNLOGGED_SESSION_LOOKBACK_DAYS } from '../../utils/actiesUtils.js';

// Fitness tracking deel 5: volledige lijst achter de "Bekijk alles"-link op
// het dashboard. De dataset is al begrensd door coach + lookbackvenster
// (fetchNietGelogdeSessies), dus client-side pagineren volstaat — geen
// aparte RPC met server-side total_count nodig, zoals bij BroadcastPushPage.
const PAGINA_GROOTTE = 20;

function formatLessonMoment(isoStr) {
  if (!isoStr) return '';
  return new Intl.DateTimeFormat('nl-NL', {
    timeZone: 'Europe/Amsterdam', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(new Date(isoStr));
}

export default function NietGelogdeSessiesPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagina, setPagina] = useState(0);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchNietGelogdeSessies(user.id, role);
      setItems(data);
      setPagina(0);
    } catch (err) {
      setError(t('nietGelogdeSessies.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id, role]);

  useEffect(() => { load(); }, [load]);

  const maxPagina = Math.max(Math.ceil(items.length / PAGINA_GROOTTE) - 1, 0);
  const paginaItems = useMemo(
    () => items.slice(pagina * PAGINA_GROOTTE, pagina * PAGINA_GROOTTE + PAGINA_GROOTTE),
    [items, pagina]
  );

  return (
    <div className="niet-gelogde-sessies">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('nietGelogdeSessies.titel')}</h1>
          <p className="page-subtitle">{t('nietGelogdeSessies.subtitel', { dagen: UNLOGGED_SESSION_LOOKBACK_DAYS })}</p>
        </div>
        <button className="btn btn-ghost" onClick={() => navigate('/coach/dashboard')}>{t('nietGelogdeSessies.terugNaarDashboard')}</button>
      </header>

      {error && <div className="error-msg">{error}</div>}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : items.length === 0 ? (
        <section className="card action-center-empty">
          <span className="action-center-empty-mark">✓</span>
          <h2>{t('nietGelogdeSessies.geenSessies')}</h2>
        </section>
      ) : (
        <section className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('nietGelogdeSessies.kolomClient')}</th>
                  <th>{t('nietGelogdeSessies.kolomMoment')}</th>
                  <th>{t('nietGelogdeSessies.kolomCoach')}</th>
                  <th aria-label={t('nietGelogdeSessies.kolomActie')}></th>
                </tr>
              </thead>
              <tbody>
                {paginaItems.map(item => (
                  <tr key={`${item.lessonId}:${item.clientId}`}>
                    <td>{item.clientName || t('acties.onbekendeClient')}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatLessonMoment(item.startsAt)}</td>
                    <td>{item.coachName || '—'}</td>
                    <td>
                      <button className="btn btn-ghost btn-sm" onClick={() => navigate('/coach/rooster')}>
                        {t('nietGelogdeSessies.naarRooster')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
            <button className="btn btn-ghost" disabled={pagina <= 0} onClick={() => setPagina(p => p - 1)}>
              {t('nietGelogdeSessies.paginaVorige')}
            </button>
            <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              {t('nietGelogdeSessies.paginaVan', { pagina: pagina + 1, totaal: maxPagina + 1 })}
            </span>
            <button className="btn btn-ghost" disabled={pagina >= maxPagina} onClick={() => setPagina(p => p + 1)}>
              {t('nietGelogdeSessies.paginaVolgende')}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
